import type { SelectedCell } from "@relex-solutions/fragments/Table"
import type { TFunction } from "i18next"
import { CUSTOMER_COLUMN_IDS } from "../constants/customerTableColumns"
import type { Customer } from "../types/Customer"

/** Maps column keys to their translated titles */
export const getColumnHeaders = (cells: SelectedCell<Customer>[], t: TFunction): string[] => {
  const uniqueKeys = Array.from(new Set(cells.map((cell) => cell.key)))

  return uniqueKeys.map((key) => {
    switch (key) {
      case CUSTOMER_COLUMN_IDS.NAME:
        return t("customers.table.name")
      case CUSTOMER_COLUMN_IDS.SALESFORCE_ID:
        return t("customers.table.salesforceId")
      case CUSTOMER_COLUMN_IDS.RELATIONSHIP_TYPE:
        return t("customers.table.relationshipType")
      case CUSTOMER_COLUMN_IDS.SUB_TYPE:
        return t("customers.table.subType")
      case CUSTOMER_COLUMN_IDS.ID:
        return t("customers.table.id")
      case CUSTOMER_COLUMN_IDS.TAX_NUMBER:
        return t("customers.table.taxNumber")
      case CUSTOMER_COLUMN_IDS.SDM_EMAIL:
        return t("customers.table.sdmEmail")
      default:
        return String(key)
    }
  })
}

/** Converts selected cells to 2D array with headers for CSV export */
export const getSelectedValuesWithHeaders = (
  inputData: Customer[],
  cells: SelectedCell<Customer>[],
  t: TFunction
): string[][] => {
  if (cells.length === 0) return []

  const uniqueKeys = Array.from(new Set(cells.map((cell) => cell.key)))

  const groupedByRow = cells.reduce<Record<number, Record<string, string>>>((acc, cell) => {
    if (!acc[cell.rowIndex]) acc[cell.rowIndex] = {}
    const cellValue = inputData[cell.rowIndex]?.[cell.key]
    acc[cell.rowIndex][String(cell.key)] = String(cellValue ?? "")
    return acc
  }, {})

  const headers = getColumnHeaders(cells, t)

  const dataRows = Object.keys(groupedByRow)
    .sort((a, b) => Number(a) - Number(b))
    .map((rowIndex) => uniqueKeys.map((key) => groupedByRow[Number(rowIndex)][String(key)] || ""))

  return [headers, ...dataRows]
}
